import type { AppearanceStyle } from "../shared/appearance";
import type { StyleRadii } from "./types";

/** Corner radii each style draws with when the admin sets none. */
export const STYLE_RADII: Readonly<Record<AppearanceStyle, StyleRadii>> = {
  classic: { modal: "8px", button: "8px", panel: "8px", control: "6px" },
  minimal: { modal: "4px", button: "4px", panel: "4px", control: "4px" },
  soft: { modal: "24px", button: "999px", panel: "16px", control: "12px" },
  glass: { modal: "28px", button: "999px", panel: "20px", control: "12px" },
};

/** Brand accent when neither the host nor the notice sets one. */
export const DEFAULT_ACCENT_COLOR = "#4f87ff";

export const DEFAULT_HEADING_COLOR = "#323B4B";
export const DEFAULT_TEXT_COLOR = "#344054";

/** The purposes heading when the notice sets none. */
export const DEFAULT_PURPOSE_SECTION_HEADING = "Manage What You Share";

/**
 * Glass's desktop card. A percentage, not viewport units, so a host that
 * frames the notice gets a card that fits the frame.
 */
export const GLASS_DESKTOP_CARD_WIDTH = "min(640px, calc(100% - 48px))";

/** Space between the panels on a sheet: purposes, the sections, the OTP step. */
export const SHEET_PANEL_GAP = "10px";

/** Classic's own colours, under the parts a layout adds to an opaque style. */
export const SOLID_SURFACE_DEFAULTS = {
  panel: "#ffffff",
  edge: "#e4e7ec",
  outline: "#d0d5dd",
} as const;

/** localStorage key prefix for the last appearance served per notice. */
export const APPEARANCE_CACHE_KEY_PREFIX = "redacto_notice_appearance:";

export const LAYOUT_STYLE_ID = "redacto-notice-layout-styles";
export const GLASS_STYLE_ID = "redacto-notice-glass-styles";
export const SWITCH_STYLE_ID = "redacto-notice-switch-styles";

/** Set on the notice root while it is docked to the bottom edge as a sheet. */
export const DOCK_ATTRIBUTE = "data-redacto-sheet";
export const DOCKED = "dock";

/** Set on the notice root when the console turns motion off. */
export const MOTION_ATTRIBUTE = "data-redacto-motion";
export const MOTION_OFF = "off";

/** Set on every root of one notice; the admin's custom CSS is scoped to it. */
export const NOTICE_ID_ATTRIBUTE = "data-redacto-notice-id";

/** Marks the `<style>` holding a notice's custom CSS, valued with its id. */
export const CUSTOM_CSS_ATTRIBUTE = "data-redacto-notice-css";

/** Set on the notice root when purpose checkboxes are drawn as switches. */
export const CONTROL_ATTRIBUTE = "data-redacto-control";
export const SWITCH_CONTROL = "switch";

/** Track and thumb sizes, per checkbox size, in px. */
export const SWITCH_SIZES = {
  large: { width: 44, height: 26, thumb: 20 },
  medium: { width: 40, height: 24, thumb: 18 },
} as const;

export const SWITCH_THUMB_INSET = 3;

/** A px length, capturing the number: `"14px"` → `"14"`. */
export const PX_LENGTH_RE = /^(\d+(?:\.\d+)?)px$/;

/** Scaled font sizes keep one decimal place. */
export const FONT_SIZE_PRECISION = 10;

/** Media query for the device's dark scheme, which automatic dark mode follows. */
export const DARK_SCHEME_QUERY = "(prefers-color-scheme: dark)";

/** Gap between a corner card and the viewport edges. */
export const CORNER_OFFSET = "24px";

/** A bottom bar spans the page; its content stays at a readable width. */
export const BOTTOM_BAR_CONTENT_WIDTH = "1100px";

/** Logo width cap as a multiple of its height: classic's 140px over 32px. */
export const LOGO_WIDTH_RATIO = 4.375;

/** Longest accessible name for a section header whose title is the notice copy itself. */
export const ACCORDION_NAME_MAX_CHARS = 80;

/** Stable hooks the admin's custom CSS targets, as `[data-redacto-part="…"]`. */
export const PART_ATTRIBUTE = "data-redacto-part";

export const NOTICE_PARTS = [
  "root",
  "overlay",
  "header",
  "logo",
  "title",
  "language",
  "notice-text",
  "purposes",
  "product",
  "purpose",
  "purpose-title",
  "data-element",
  "section",
  "dpo",
  "footer",
  "accept-all",
  "accept-selected",
  "decline",
  "confirm-dialog",
  "otp",
] as const;

/** Keyframe names are global, so they carry the notice in their name. */
export const NOTICE_KEYFRAMES = {
  rise: "redacto-notice-rise",
  float: "redacto-notice-float",
  /**
   * The float frames under a second name, for the desktop loader pill. The pill
   * and the card are one element; only a change of animation-name restarts the
   * entrance when the card replaces the pill.
   */
  floatPill: "redacto-notice-float-pill",
  fade: "redacto-notice-fade",
  settle: "redacto-notice-settle",
  pop: "redacto-notice-pop",
  spin: "redacto-notice-spin",
} as const;

/** Classes the glass notice puts on rows it separates with hairlines. */
export const GLASS_ROW_CLASS = "redacto-glass-row";

/**
 * Custom properties the glass stylesheet reads; the notice root sets them from
 * its palette.
 */
export const GLASS_VARS = {
  accent: "--redacto-glass-accent",
  accentMid: "--redacto-glass-accent-mid",
  accentFaint: "--redacto-glass-accent-faint",
  track: "--redacto-glass-track",
  outline: "--redacto-glass-outline",
  hairline: "--redacto-glass-hairline",
  control: "--redacto-glass-control",
} as const;
